"use client"

import * as React from "react"
import { Users, Plus, Trash2, ShieldCheck, AlertTriangle, Search, Check, ChevronDown, UserPlus } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

interface WorkspaceMembersManagerProps {
  workspaceId: string
  workspaceName: string
  allUsers: any[]
}

export function WorkspaceMembersManager({
  workspaceId,
  workspaceName,
  allUsers,
}: WorkspaceMembersManagerProps) {
  const [members, setMembers] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchMemberQuery, setSearchMemberQuery] = React.useState("")
  const [userToRemove, setUserToRemove] = React.useState<{ userId: string; name: string } | null>(null)
  const [isRemoving, setIsRemoving] = React.useState(false)

  const fetchMembers = React.useCallback(async () => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return
    setLoading(true)
    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            query GetWorkspaceMembers($workspaceId: ID!) {
              workspaceMembers(workspaceId: $workspaceId) {
                id
                userId
                role
                user {
                  id
                  name
                  email
                  avatarUrl
                }
              }
            }
          `,
          variables: { workspaceId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.workspaceMembers) {
        setMembers(result.data.workspaceMembers)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  React.useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const handleAddMember = async (userId: string, role = "member") => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation AddWorkspaceMember($workspaceId: ID!, $userId: ID!, $role: String) {
              addWorkspaceMember(workspaceId: $workspaceId, userId: $userId, role: $role)
            }
          `,
          variables: { workspaceId, userId, role }
        })
      })
      const result = await res.json()
      if (result.data && result.data.addWorkspaceMember) {
        toast.success("Membro adicionado ao ambiente!")
        fetchMembers()
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bold_users_invalidated"))
        }
      } else {
        toast.error("Erro ao adicionar membro")
      }
    } catch (e) {
      toast.error("Erro de conexão")
    }
  }

  const handleUpdateRole = async (userId: string, newRole: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation UpdateWorkspaceMemberRole($workspaceId: ID!, $userId: ID!, $role: String!) {
              updateWorkspaceMemberRole(workspaceId: $workspaceId, userId: $userId, role: $role)
            }
          `,
          variables: { workspaceId, userId, role: newRole }
        })
      })
      const result = await res.json()
      if (result.data && result.data.updateWorkspaceMemberRole) {
        toast.success("Papel no ambiente atualizado!")
        setMembers(prev =>
          prev.map(m => (m.userId === userId ? { ...m, role: newRole } : m))
        )
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bold_users_invalidated"))
        }
      } else if (result.errors && result.errors[0]) {
        toast.error(result.errors[0].message)
      }
    } catch (e) {
      toast.error("Erro ao atualizar papel")
    }
  }

  const handleRemoveMember = async (userId: string) => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    if (!token) return

    try {
      const res = await fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: `
            mutation RemoveWorkspaceMember($workspaceId: ID!, $userId: ID!) {
              removeWorkspaceMember(workspaceId: $workspaceId, userId: $userId)
            }
          `,
          variables: { workspaceId, userId }
        })
      })
      const result = await res.json()
      if (result.data && result.data.removeWorkspaceMember) {
        toast.success("Membro removido do ambiente!")
        setMembers(prev => prev.filter(m => m.userId !== userId))
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bold_users_invalidated"))
        }
      } else if (result.errors && result.errors[0]) {
        toast.error(result.errors[0].message)
      }
    } catch (e) {
      toast.error("Erro ao remover membro")
    }
  }

  // Identificar usuários que são Administradores ou Proprietários da EMPRESA
  const companyAdminsAndOwners = React.useMemo(() => {
    return (allUsers || []).filter(u => {
      const r = u.accountRole || u.companyRole || u.role
      return r === "owner" || r === "admin"
    })
  }, [allUsers])

  const companyAdminUserIds = React.useMemo(() => {
    const set = new Set<string>()
    companyAdminsAndOwners.forEach(u => {
      if (u.id) set.add(String(u.id))
      if (u.userId) set.add(String(u.userId))
    })
    return set
  }, [companyAdminsAndOwners])

  const companyAdminEmails = React.useMemo(() => {
    const set = new Set<string>()
    companyAdminsAndOwners.forEach(u => {
      if (u.email) set.add(String(u.email).toLowerCase())
      if (u.user?.email) set.add(String(u.user.email).toLowerCase())
    })
    return set
  }, [companyAdminsAndOwners])

  // Lista consolidada de membros garantindo inclusão dos admins/owners da empresa
  const displayMembers = React.useMemo(() => {
    const list = [...members]
    companyAdminsAndOwners.forEach(adminUser => {
      const uid = String(adminUser.userId || adminUser.id || '')
      const email = String(adminUser.email || adminUser.user?.email || '').toLowerCase()

      const existingIndex = list.findIndex(m => {
        const mUid = String(m.userId || m.id || '')
        const mEmail = String(m.user?.email || m.email || '').toLowerCase()
        return (uid && mUid === uid) || (email && mEmail === email)
      })

      if (existingIndex >= 0) {
        list[existingIndex] = {
          ...list[existingIndex],
          role: "admin",
          isCompanyAdminOrOwner: true,
        }
      } else {
        list.unshift({
          id: `company-admin-${uid}`,
          userId: uid,
          role: "admin",
          isCompanyAdminOrOwner: true,
          user: {
            id: uid,
            name: adminUser.name || adminUser.user?.name,
            email: adminUser.email || adminUser.user?.email,
            avatarUrl: adminUser.avatarUrl || adminUser.user?.avatarUrl,
            role: adminUser.role || adminUser.accountRole
          }
        })
      }
    })
    return list
  }, [members, companyAdminsAndOwners])

  const assignedUserIds = displayMembers.map(m => String(m.userId))
  const availableUsers = React.useMemo(() => {
    return (allUsers || []).filter(u => {
      const uid = String(u.id || u.userId || '')
      const uEmail = String(u.email || u.user?.email || '').toLowerCase()
      const isCompanyAdmin = companyAdminUserIds.has(uid) || (uEmail !== '' && companyAdminEmails.has(uEmail))
      return !isCompanyAdmin && !assignedUserIds.includes(uid)
    })
  }, [allUsers, companyAdminUserIds, companyAdminEmails, assignedUserIds])

  const filteredAvailableUsers = React.useMemo(() => {
    if (!searchMemberQuery.trim()) return availableUsers
    const q = searchMemberQuery.toLowerCase().trim()
    return availableUsers.filter(u =>
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    )
  }, [availableUsers, searchMemberQuery])

  return (
    <div className="space-y-4 font-sans">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h5 className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
            <Users className="size-3.5 text-[#004EB1]" />
            Membros e Permissões do Ambiente
          </h5>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Gerencie os usuários que têm permissão de acesso a este ambiente.
          </p>
        </div>

        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                size="sm"
                className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-8 px-3.5 gap-1.5 cursor-pointer shadow-xs"
              >
                <UserPlus className="size-3.5" />
                <span>Adicionar Membro</span>
              </Button>
            }
          />
          <PopoverContent align="end" className="w-72 p-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl">
            <div className="px-1 py-1">
              <p className="text-[11px] font-bold text-zinc-700 dark:text-zinc-300">
                Adicionar Usuário ao Ambiente
              </p>
              <p className="text-[10px] text-zinc-400">
                Selecione um usuário da empresa para vincular.
              </p>
            </div>

            {availableUsers.length > 5 && (
              <div className="relative my-2 px-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3 text-zinc-400" />
                <input
                  type="text"
                  value={searchMemberQuery}
                  onChange={(e) => setSearchMemberQuery(e.target.value)}
                  placeholder="Buscar usuário..."
                  className="w-full pl-7 pr-2.5 py-1 text-xs rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-white outline-none focus:border-[#004EB1]"
                />
              </div>
            )}

            {filteredAvailableUsers.length === 0 ? (
              <div className="py-4 text-center">
                <p className="text-xs text-zinc-400 italic">
                  {availableUsers.length === 0
                    ? "Todos os usuários da empresa já têm acesso a este ambiente."
                    : "Nenhum usuário encontrado com esta busca."}
                </p>
              </div>
            ) : (
              <div className="space-y-1 max-h-52 overflow-y-auto pr-1 mt-1">
                {filteredAvailableUsers.map(u => {
                  const uid = u.id || u.userId
                  return (
                    <button
                      key={uid}
                      type="button"
                      onClick={() => {
                        handleAddMember(uid, "member")
                        setIsOpen(false)
                        setSearchMemberQuery("")
                      }}
                      className="w-full text-left p-2 rounded-lg hover:bg-blue-50/60 dark:hover:bg-blue-950/40 border border-transparent hover:border-blue-100 dark:hover:border-blue-900/50 flex items-center justify-between text-xs cursor-pointer transition-all group"
                    >
                      <div className="flex items-center gap-2 truncate pr-2">
                        <div className="size-6 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex items-center justify-center font-bold text-[10px] shrink-0">
                          {(u.name || u.email || "?")[0].toUpperCase()}
                        </div>
                        <div className="truncate">
                          <p className="font-semibold text-zinc-900 dark:text-white truncate text-[11px] group-hover:text-[#004EB1]">
                            {u.name || "Usuário"}
                          </p>
                          <p className="text-[10px] text-zinc-400 truncate">{u.email}</p>
                        </div>
                      </div>
                      <Plus className="size-3.5 text-zinc-400 group-hover:text-[#004EB1] shrink-0" />
                    </button>
                  )
                })}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden bg-white dark:bg-zinc-900">
        <div className="px-4 py-2.5 bg-zinc-50 dark:bg-zinc-950 border-b border-zinc-200/80 dark:border-zinc-800 flex items-center justify-between text-[11px] font-semibold text-zinc-500">
          <span>Usuário ({displayMembers.length})</span>
          <span>Papel de Acesso</span>
        </div>

        {displayMembers.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-400 italic">
            Nenhum membro configurado especificamente.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100 dark:divide-zinc-800/80 max-h-[300px] overflow-y-auto">
            {displayMembers.map(m => {
              const companyUser = (allUsers || []).find(u => {
                const uid = String(u.id || u.userId || '')
                const mUid = String(m.userId || '')
                const uEmail = String(u.email || u.user?.email || '').toLowerCase()
                const mEmail = String(m.user?.email || m.email || '').toLowerCase()
                return (uid !== '' && uid === mUid) || (uEmail !== '' && uEmail === mEmail)
              })

              const usr = companyUser || m.user || m

              const memberUserId = String(m.userId || usr?.id || usr?.userId || '')
              const memberEmail = String(usr?.email || m.user?.email || m.email || '').toLowerCase()
              const companyUserRole = companyUser?.accountRole || companyUser?.companyRole || companyUser?.role

              const isCompanyAdminOrOwner =
                companyAdminUserIds.has(memberUserId) ||
                (memberEmail !== '' && companyAdminEmails.has(memberEmail)) ||
                companyUserRole === "owner" ||
                companyUserRole === "admin" ||
                usr?.accountRole === "owner" ||
                usr?.accountRole === "admin" ||
                usr?.companyRole === "owner" ||
                usr?.companyRole === "admin" ||
                usr?.role === "owner" ||
                usr?.role === "admin" ||
                Boolean(m.isCompanyAdminOrOwner)

              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between px-4 py-2.5 hover:bg-zinc-50/60 dark:hover:bg-zinc-800/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="size-8 rounded-full bg-blue-50 dark:bg-blue-950/60 text-[#004EB1] dark:text-blue-400 flex items-center justify-center font-bold text-xs shrink-0 border border-blue-100 dark:border-blue-900/40">
                      {(usr?.name || usr?.email || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs font-semibold text-zinc-900 dark:text-white leading-tight">
                          {usr?.name || "Usuário"}
                        </p>
                        {isCompanyAdminOrOwner && (
                          <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200/60 dark:border-amber-900/40 select-none">
                            Empresa
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-zinc-400">{usr?.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isCompanyAdminOrOwner ? (
                      <div
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200/80 dark:border-zinc-700 shadow-2xs select-none"
                        title="Administradores e Proprietários da Empresa possuem acesso irrestrito a todos os ambientes."
                      >
                        <ShieldCheck className="size-3.5 text-[#004EB1] dark:text-blue-400" />
                        <span>Admin</span>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <select
                            value={m.role || "member"}
                            onChange={e => handleUpdateRole(m.userId, e.target.value)}
                            className="appearance-none text-xs font-semibold bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg pl-3 pr-7 py-1.5 text-zinc-800 dark:text-zinc-200 cursor-pointer outline-none focus:border-[#004EB1]"
                          >
                            <option value="admin">Administrador</option>
                            <option value="manager">Gerente</option>
                            <option value="member">Membro</option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-3 text-zinc-400 pointer-events-none" />
                        </div>

                        <button
                          type="button"
                          onClick={() => setUserToRemove({ userId: m.userId, name: usr?.name || usr?.email || "Usuário" })}
                          className="text-zinc-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-955/30 transition-colors cursor-pointer"
                          title="Remover acesso a este ambiente"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Remoção de Membro */}
      {userToRemove && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-sm bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl p-5 space-y-4 animate-in zoom-in-95 duration-150 font-sans">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                <AlertTriangle className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Remover do Ambiente</h4>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{workspaceName || "Ambiente"}</p>
              </div>
            </div>

            <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
              Tem certeza que deseja remover <strong className="text-zinc-900 dark:text-white font-semibold">{userToRemove.name}</strong> deste ambiente? O usuário não terá mais acesso a este ambiente de trabalho.
            </p>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setUserToRemove(null)}
                disabled={isRemoving}
                className="text-xs h-8 px-3 cursor-pointer"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isRemoving}
                onClick={async () => {
                  setIsRemoving(true)
                  try {
                    await handleRemoveMember(userToRemove.userId)
                    setUserToRemove(null)
                  } finally {
                    setIsRemoving(false)
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8 px-4 cursor-pointer"
              >
                {isRemoving ? "Removendo..." : "Confirmar Remoção"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
